import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, exhaustMap, catchError, tap, switchMap, mergeMap, filter } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthState, TwoFactorChallenge } from './auth.reducer';
import * as AuthActions from './auth.actions';
import * as MainActions from '../main/main.actions';
import { ApiService } from '../../shared/services/api.service';
import { of, EMPTY, from } from 'rxjs';
import { LoaderService } from '../../shared/services/loader/loader.service';
import { TwoFactorChallengeResponse, User } from '../../shared/models/loginResponse';
import { Domain } from '../../shared/models/domain';
import { forgetTrustedBrowser, readTrustedBrowser, saveTrustedBrowser } from '../../shared/helpers/trusted-browser';
import { retryAfterSeconds } from '../../shared/helpers/sign-in-wait';
import * as AuthSelectors from './auth.selectors';
import * as AppActions from '../app/app.actions';
import { Router } from '@angular/router';
import { SessionRefreshService } from '../../shared/services/session-refresh.service';
import { PushService } from '../../shared/services/push.service';

@Injectable()
export class AuthEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly apiService: ApiService,
    private readonly store: Store<AuthState>,
    private readonly loaderService: LoaderService,
    private readonly router: Router,
    private readonly sessionRefresh: SessionRefreshService,
    private readonly push: PushService,
  ) { }

  readonly startSessionRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(() => this.sessionRefresh.start())
    ),
    { dispatch: false }
  );

  readonly stopSessionRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => this.sessionRefresh.stop())
    ),
    { dispatch: false }
  );

  readonly startPush$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(() => this.push.start())
    ),
    { dispatch: false }
  );

  readonly stopPush$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => this.push.stop())
    ),
    { dispatch: false }
  );

  readonly tryLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.requestLogin),
      tap(() => this.loaderService.show()),
      exhaustMap(action => from(readTrustedBrowser(action.username)).pipe(
        switchMap(trustedBrowser =>
          this.apiService.login({ username: action.username, password: action.password, bearer: 'true' }, trustedBrowser).pipe(
            map(loginResponse => {
              if (!loginResponse.TwoFactor) {
                return AuthActions.loginSuccess(loginResponse);
              }

              this.loaderService.hide();

              // Sent and refused: expired, withdrawn, or older than the password.
              if (trustedBrowser) {
                void forgetTrustedBrowser(action.username);
              }

              return AuthActions.loginChallenge({ challenge: AuthEffects.challengeOf(action.username, loginResponse.TwoFactor) });
            }),
            catchError(err => {
              this.loaderService.hide();

              if (err?.status === 429) {
                return of(AuthEffects.locked(err));
              }

              if (err?.status === 503 && err?.error?.Message === 'Code not sent') {
                return of(AuthActions.setError({ error: 'LOGIN.CODE_NOT_SENT' }));
              }

              if (err?.status === 503 && err?.error?.Message === 'Two-factor not available') {
                return of(AuthActions.setError({ error: 'LOGIN.CODE_UNAVAILABLE' }));
              }

              if (err?.status === 403 && err?.error?.Message === 'DOMAIN_DISABLED') {
                return of(AuthActions.setError({ error: 'LOGIN.DOMAIN_DISABLED' }));
              }

              return of(AuthActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' }))
            })
          )
        )
      ))
    ),
    { dispatch: true }
  );

  readonly verifyCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.requestTwoFactorVerify),
      concatLatestFrom(() => this.store.select(AuthSelectors.getChallenge)),
      filter(([, challenge]) => !!challenge),
      exhaustMap(([action, challenge]) => {
        this.loaderService.show();

        const verify = action.recovery
          ? this.apiService.twoFactorRecovery(challenge.ticket, action.code)
          : this.apiService.twoFactorVerify(challenge.ticket, action.code, action.trust);

        return verify.pipe(
          map(response => {
            if (response.Setup === 'required') {
              this.loaderService.hide();
              return AuthActions.twoFactorSetupRequired({ ticket: response.Ticket, username: challenge.username });
            }

            if (response.Trust) {
              void saveTrustedBrowser(challenge.username, response.Trust);
            }

            return AuthActions.loginSuccess({ Output: response.Output, Bearer: response.Bearer });
          }),
          catchError(err => {
            this.loaderService.hide();
            return of(AuthEffects.codeRefused(err));
          })
        );
      })
    )
  );

  readonly resendCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.requestTwoFactorResend),
      concatLatestFrom(() => this.store.select(AuthSelectors.getChallenge)),
      filter(([, challenge]) => !!challenge),
      exhaustMap(([, challenge]) =>
        this.apiService.twoFactorResend(challenge.ticket).pipe(
          map(response => AuthActions.twoFactorResent({
            resendAt: Date.now() + response.ResendAfter * 1000,
            expiresAt: Date.now() + response.ExpiresIn * 1000
          })),
          catchError(err => of(AuthEffects.resendRefused(err, challenge)))
        )
      )
    )
  );

  private static challengeOf(username: string, twoFactor: TwoFactorChallengeResponse): TwoFactorChallenge {
    const now: number = Date.now();
    const resendSeconds: number = twoFactor.ResendAfter ?? 0;

    return {
      username,
      ticket: twoFactor.Ticket,
      method: twoFactor.Method,
      emailHint: twoFactor.EmailHint ?? null,
      validMinutes: Math.round(twoFactor.ExpiresIn / 60),
      expiresAt: now + twoFactor.ExpiresIn * 1000,
      resendSeconds,
      resendAt: now + resendSeconds * 1000,
      resent: false,
      attemptsLeft: null,
      error: null
    };
  }

  // Too many wrong passwords or codes for the account, or calls from the address.
  private static locked(err: HttpErrorResponse): Action {
    return AuthActions.loginLocked({ until: Date.now() + retryAfterSeconds(err) * 1000 });
  }

  // A wrong code keeps the ticket while attempts are left; the last one, like
  // an expired or used ticket, sends the user back to the password.
  private static codeRefused(err: HttpErrorResponse): Action {
    const message: unknown = err?.error?.Message;
    const attemptsLeft: number = Number(err?.error?.AttemptsLeft);

    if (err?.status === 429) {
      return AuthEffects.locked(err);
    }

    if (message === 'Invalid code' && attemptsLeft > 0) {
      return AuthActions.twoFactorFailed({ error: 'LOGIN.CODE_WRONG', attemptsLeft });
    }

    if (message === 'Invalid code') {
      return AuthActions.twoFactorCancelled({ error: 'LOGIN.CODE_TOO_MANY' });
    }

    if (message === 'Code expired') {
      return AuthActions.twoFactorCancelled({ error: 'LOGIN.CODE_EXPIRED' });
    }

    return AuthActions.twoFactorFailed({ error: 'LOGIN.CODE_UNAVAILABLE' });
  }

  // A 429 with RetryAfter is a wait; without it the ticket sent all it may and
  // the last code still works. A failed email has already replaced the code,
  // so the next try waits as after a send.
  private static resendRefused(err: HttpErrorResponse, challenge: TwoFactorChallenge): Action {
    const message: unknown = err?.error?.Message;
    const retryAfter: number = Number(err?.error?.RetryAfter);

    if (err?.status === 429) {
      return retryAfter > 0
        ? AuthActions.twoFactorResendFailed({ resendAt: Date.now() + retryAfter * 1000, error: null })
        : AuthActions.twoFactorResendFailed({ resendAt: null, error: 'LOGIN.RESEND_LIMIT' });
    }

    if (message === 'Code expired') {
      return AuthActions.twoFactorCancelled({ error: 'LOGIN.CODE_EXPIRED' });
    }

    if (message === 'Code not sent') {
      return AuthActions.twoFactorResendFailed({
        resendAt: Date.now() + challenge.resendSeconds * 1000,
        error: 'LOGIN.CODE_NOT_SENT'
      });
    }

    return AuthActions.twoFactorFailed({ error: 'LOGIN.CODE_UNAVAILABLE' });
  }

  /* readonly getToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      exhaustMap(() =>
        this.apiService.getToken().pipe(
          map(token => AuthActions.setToken({ token }),
            catchError(err => {
              this.loaderService.hide();
              return of(AuthActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' }))
            })
          ))
      )
    ),
    { dispatch: true }
  ); */

  readonly logged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      exhaustMap(() =>
        this.apiService.getAvailableDomains().pipe(
          map((domains: Domain[]) => AuthActions.setAvailablesDomains({ domains: AuthEffects.flatten(domains) })),
          catchError(err => {
            this.loaderService.hide();
            return of(AuthActions.loginAborted({ error: err?.error?.Message ? err.error.Message : 'customError' }))
          })
        )
      )
    ),
    { dispatch: true }
  );

  readonly reloadDomains$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.reloadAvailableDomains),
      switchMap(() => this.apiService.getAvailableDomains().pipe(
        map((domains: Domain[]) => AuthActions.availableDomainsReloaded({ domains: AuthEffects.flatten(domains) })),
        catchError(() => EMPTY)
      ))
    )
  );

  // The domain tree as one list, every parent before its children.
  private static flatten(domains: Domain[] | null | undefined): Domain[] {
    return (domains ?? []).flatMap(domain => [{ ...domain }, ...AuthEffects.flatten(domain.Childs)]);
  }

  readonly domain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setAvailablesDomains),
      concatLatestFrom(() => this.store.select(AuthSelectors.getUser)),
      filter(([, user]) => !!user),
      map(([domains, user]) => {
        const domainId = user.Domain?.Id ? user.Domain.Id : user.Properties?.CurrentDomain;

        return domainId === undefined
          ? AuthActions.setCurrentDomainProperty({ domain: domains.domains[0] })
          : AuthActions.initSetUserDomain(domains.domains.find(x => x.Id == domainId));
      }),
    ),
    { dispatch: true }
  );

  /* readonly multipleDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.multipleDomain),
      tap(() => this.router.navigate(['auth/choose-domain'])),
      tap(() => this.loaderService.hide())
    ),
    { dispatch: false }
  ); */

  readonly setCurrentDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setCurrentDomainProperty),
      exhaustMap((domain) => {
        return this.apiService.addUserProperty({ Name: 'CurrentDomain', Value: domain.domain.Id }).pipe(
          switchMap(() => [
            MainActions.setDevices({ devices: null }),
            AuthActions.initSetUserDomain(domain.domain)
          ]),
          catchError(err => {
            this.loaderService.hide();
            return of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' }))
          })
        )
      }),
    ),
    { dispatch: true }
  );

  readonly setUserDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initSetUserDomain),
      // tap(() => this.loaderService.show()),
      exhaustMap((domain) => {
        return this.apiService.setUserDomain(domain.Id).pipe(
          map((res) => AuthActions.setUserDomain({ domain: domain })),
          catchError(err => {
            this.loaderService.hide();
            return of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' }))
          })
        )
      }),
    ),
    { dispatch: true }
  );

  readonly setUserInfo$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.initSetUserInfo),
        exhaustMap(() => {
          return this.apiService.getUserInfo().pipe(
            map((user) => AuthActions.setUserInfo(user)),
            catchError(err => {
              this.loaderService.hide();
              return of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' }))
            })
          )
        }),
      ),
      { dispatch: true }
  );

  readonly setUserRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setUserDomain),
      exhaustMap((setDomainRes) =>
        this.apiService.setUserRoles(setDomainRes.domain.Id).pipe(
          switchMap(res => [
            AuthActions.setUserRoles({ roles: res.Roles }),
            AuthActions.getCurrentDomainProperties({ domainId: setDomainRes.domain.Id }),
            AuthActions.initSetUserInfo(),
          ]),
          catchError(err => {
            this.loaderService.hide();
            return of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' }))
          })
        )
      )
    ),
    { dispatch: true }
  )

  readonly goToMainAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setUserInfo),
      tap(() => {
        // On a hard reload the bootstrap chain re-fires setUserInfo before
        // the router has restored the deep-linked URL, so this.router.url is
        // still '/' and we'd wrongly bounce to the dashboard. window.location
        // .pathname already reflects the reloaded URL (base href is '/'), so
        // only redirect from non-/main entry points (i.e. fresh login).
        if (!window.location.pathname.startsWith('/main')) {
          this.router.navigate(['main']);
        }
      })
    ),
    { dispatch: false }
  )

  readonly loadCurrentDomainProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getCurrentDomainProperties),
      exhaustMap((result) =>
        this.apiService.getDomainProperties(result.domainId).pipe(
          switchMap(res => [
            AuthActions.loadCurrentDomainProperties({ properties: res }),
          ]),
          catchError(err => {
            this.loaderService.hide();
            return of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' }))
          })
        )
      )
    ),
    { dispatch: true }
  );

  // Before the logout reducer runs, so the call still carries the bearer the
  // server has to withdraw. The session ends either way.
  readonly logoutRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutRequested),
      exhaustMap(() =>
        this.apiService.tryLogout().pipe(
          map(() => AuthActions.logout()),
          catchError(() => of(AuthActions.logout()))
        )
      )
    )
  );

  // The bearer is withdrawn while the store still carries it, then dropped as
  // on a logout (refresh timer, push, secure storage); the login page says why.
  readonly loginAborted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginAborted),
      exhaustMap(({ error }) =>
        this.apiService.tryLogout().pipe(
          catchError(() => of(null)),
          switchMap(() => [AuthActions.logout(), AuthActions.setError({ error })])
        )
      )
    )
  );

  readonly logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => this.router.navigate(['/'])),
      map(() => AppActions.resetReducers())
    )
  );

  readonly toggleDarkThemeState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.toggleDarkTheme),
      exhaustMap(res =>
        this.apiService.addUserProperty({ Name: 'DarkThemeActive', Value: res.darkModeChange }).pipe(
          exhaustMap(() => EMPTY),
          catchError(err => of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })))
        )
      )
    ),
    { dispatch: true }
  );

  readonly changeProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changeProfileImage),
      exhaustMap(res =>
        this.apiService.addUserProperty({ Name: 'ProfileImage', Value: res.base64Image }).pipe(
          exhaustMap(() => EMPTY),
          catchError(err => of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })))
        )
      )
    ),
    { dispatch: true }
  );

  readonly changeProfileLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changeProfileLanguage),
      exhaustMap(res =>
        this.apiService.addUserProperty({ Name: 'FavouriteLanguage', Value: res.favouriteLanguage }).pipe(
          exhaustMap(() => EMPTY),
          catchError(err => of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })))
        )
      )
    ),
    { dispatch: true }
  );

  readonly changeProfilePhoneNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changeProfilePhoneNumber),
      exhaustMap(res =>
        this.apiService.addUserProperty({ Name: 'PhoneNumber', Value: res.phoneNumber }).pipe(
          exhaustMap(() => EMPTY),
          catchError(err => of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })))
        )
      )
    ),
    { dispatch: true }
  );

  readonly changeUserPhoneNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changeUserPhoneNumber),
      exhaustMap(res =>
        this.apiService.addSpecificUserProperty(res.request).pipe(
          exhaustMap(() => EMPTY),
          catchError(err => of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })))
        )
      )
    ),
    { dispatch: true }
  );

  readonly setUserProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setUserProperty),
      mergeMap(res =>
        this.apiService.addUserProperty({ Name: res.name, Value: res.value }).pipe(
          exhaustMap(() => EMPTY),
          catchError(err => of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })))
        )
      )
    ),
    { dispatch: true }
  );

  readonly changeProfileNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changeProfileNotes),
      exhaustMap(res =>
        this.apiService.addUserProperty({ Name: 'ProfileNotes', Value: res.notes }).pipe(
          exhaustMap(() => EMPTY),
          catchError(err => of(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })))
        )
      )
    ),
    { dispatch: true }
  );
}
